import { type IconName } from '@eluve/components';
import { type MessageKey } from '@eluve/i18n';
import { type PlanDraft, planDraftSchema } from '@eluve/plan-shapes';

// The AI SDK tool-part shape as it streams into useChat. `presentPlan` carries
// the COMPLETE spec as its tool input (so the coerced prefix renders the draft
// building live); the exploration tools (fhirSearch / fhirRead) carry their
// query input and result output.
export type ToolUIPart = {
  type: string;
  toolCallId: string;
  state:
    | 'input-streaming'
    | 'input-available'
    | 'output-available'
    | 'output-error';
  input?: unknown;
  output?: unknown;
  errorText?: string;
};

// A presentPlan emission is only renderable once its full output — the spec with
// a shape discriminator — has streamed in. `output-available` can briefly carry
// a partial object, so validate against the shared draft schema: a transient
// emission with a shape but missing its shape-specific arrays would crash the
// preview renderers, so only a complete, schema-valid draft is treated as ready.
export function isPlanDraft(output: unknown): output is PlanDraft {
  return planDraftSchema.safeParse(output).success;
}

// Per-tool display copy + icon — the ToolCallStatus pattern (an active label
// while the call runs, a completed label once it settles). Plan Builder's own
// tool vocabulary, kept here rather than in the shared Chat component so that
// component stays free of feature-specific keys. Labels are i18n message keys
// resolved through `t()` at render time.
type ToolDisplay = {
  activeLabel: MessageKey;
  completedLabel: MessageKey;
  errorLabel: MessageKey;
  icon: IconName;
};

export const PLAN_BUILDER_TOOL_DISPLAY: Record<string, ToolDisplay> = {
  fhirSearch: {
    activeLabel: 'carePlans.builder.toolSearchingRecords',
    completedLabel: 'carePlans.builder.toolSearchedRecords',
    errorLabel: 'carePlans.builder.toolSearchFailed',
    icon: 'Database',
  },
  fhirRead: {
    activeLabel: 'carePlans.builder.toolReadingRecord',
    completedLabel: 'carePlans.builder.toolReadRecord',
    errorLabel: 'carePlans.builder.toolReadFailed',
    icon: 'Database',
  },
};

const DEFAULT_TOOL_DISPLAY: ToolDisplay = {
  activeLabel: 'carePlans.builder.toolWorking',
  completedLabel: 'carePlans.builder.toolDone',
  errorLabel: 'carePlans.builder.toolFailed',
  icon: 'WandSparkles',
};

export function toolDisplay(toolName: string): ToolDisplay {
  return PLAN_BUILDER_TOOL_DISPLAY[toolName] ?? DEFAULT_TOOL_DISPLAY;
}

export function toolLabel(toolName: string, running: boolean): MessageKey {
  const display = toolDisplay(toolName);
  return running ? display.activeLabel : display.completedLabel;
}

// Look-back for the validate→fix cycle: a streaming presentPlan emission is
// "resolving validation issues" exactly when a PRIOR presentPlan in the same
// assistant turn settled invalid. Every validate→fix iteration lands in one
// assistant message (one agent.stream turn), so the prior emissions are earlier
// parts of the same message. Returns the issue count of the nearest prior
// settled emission when it was invalid, else 0 (a prior valid draft, or none).
export function priorInvalidIssueCount(
  parts: ToolUIPart[],
  beforeIndex: number,
): number {
  for (let i = beforeIndex - 1; i >= 0; i--) {
    const part = parts[i];
    if (
      part?.type === 'tool-presentPlan' &&
      part.state === 'output-available' &&
      isPlanDraft(part.output)
    ) {
      return part.output.valid ? 0 : part.output.issues.length;
    }
  }
  return 0;
}
